const API_URL = process.env.REACT_APP_API_URL;

/**
 * Extract the best error message from a ProblemDetail response body, matching the
 * convention used across the other services (flowService et al.).
 */
function extractErrorMessage(errorBody, fallback) {
  if (!errorBody) return fallback;
  const detail = errorBody.detail;
  const title = errorBody.title;
  if (detail && title && title !== fallback) {
    return `${title}: ${detail}`;
  }
  return detail || fallback;
}

async function asJsonOrThrow(response, fallback) {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, fallback));
  }
  return await response.json();
}

/**
 * Client for the Submissions + delivery-destinations API (workstream A ↔ G contract in
 * docs/launch/PLAN.md). Every submission a flow receives is an "submission"; each flow can have
 * zero or more delivery destinations (email, webhook, Google Sheets and whatever connector
 * `GET /api/connectors` describes) that the backend fans the submission out to.
 */
class DeliveryService {
  /**
   * Every channel this deployment can deliver to, described rather than coded, this is what
   * `DestinationDialog` renders its connector picker and per-connector form from.
   *
   * @returns {Promise<Array<{type: string, displayName: string, supports: string[],
   *   connectionProvider: (string|null), configSchema: {fields: Array, helpText: (string|null)},
   *   requiredPlan: 'FREE'|'PRO'}>>}
   */
  async listConnectors(authenticatedFetch) {
    const response = await authenticatedFetch(`${API_URL}/api/connectors`);
    return asJsonOrThrow(response, 'Failed to load connector types');
  }

  /**
   * Options for a SELECT field whose choices come from the server rather than being static.
   *
   * The environment rides along because every choices endpoint is environment-scoped, it is
   * answering "which of *your* calendars", not "which calendars exist", and the backend declares
   * it `@RequestParam UUID envId`, i.e. required. Omitting it 400s, which the dialog used to
   * render as an empty dropdown with no explanation.
   */
  async fetchChoices(choicesEndpoint, envId, authenticatedFetch) {
    const separator = choicesEndpoint.includes('?') ? '&' : '?';
    const response = await authenticatedFetch(
      `${API_URL}${choicesEndpoint}${separator}envId=${encodeURIComponent(envId)}`);
    return asJsonOrThrow(response, 'Failed to load options');
  }

  /**
   * Page of submissions (flow submissions) for an environment, newest first.
   *
   * @returns {Promise<{items: Array, total: number}>}
   */
  async listSubmissions(envId, { flowId, page = 0, size = 50 } = {}, authenticatedFetch) {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (flowId) params.set('flowId', flowId);
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/submissions?${params.toString()}`
    );
    return asJsonOrThrow(response, 'Failed to load submissions');
  }

  async getSubmission(envId, id, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/submissions/${id}`
    );
    return asJsonOrThrow(response, 'Failed to load the submission');
  }

  async listDestinations(envId, flowId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/destinations`
    );
    return asJsonOrThrow(response, 'Failed to load delivery destinations');
  }

  /**
   * @param {object} body {type, config, events, connectionId}, `type` is one of `GET
   *   /api/connectors`' `type`s, `events` a subset of that connector's `supports`, and
   *   `connectionId` present only for a connector with a `connectionProvider`.
   * @returns {Promise<object>} The created destination. A REVEAL_ONCE field's value (e.g. a
   *   webhook's signing secret) is only ever present on this create response, the caller must
   *   show it once.
   */
  async createDestination(envId, flowId, body, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/destinations`,
      { method: 'POST', body: JSON.stringify(body) }
    );
    return asJsonOrThrow(response, 'Failed to add the destination');
  }

  async updateDestination(envId, flowId, destinationId, body, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/destinations/${destinationId}`,
      { method: 'PUT', body: JSON.stringify(body) }
    );
    return asJsonOrThrow(response, 'Failed to update the destination');
  }

  async deleteDestination(envId, flowId, destinationId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/destinations/${destinationId}`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to delete the destination'));
    }
  }

  /** Sends a sample submission through the destination. Returns the resulting delivery attempt. */
  async testDestination(envId, flowId, destinationId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/destinations/${destinationId}/test`,
      { method: 'POST' }
    );
    return asJsonOrThrow(response, 'Test send failed');
  }

  async listDeliveries(envId, flowId, limit = 20, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/deliveries?limit=${limit}`
    );
    return asJsonOrThrow(response, 'Failed to load recent deliveries');
  }

  // ===========================================================================================
  // Environment-default destinations (docs/connectors/PLAN.md step 8). Same connectors, same
  // request/response shapes as the flow-scoped methods above minus flowId, Settings -> Deliveries
  // and the per-flow dialog's "inherited from environment defaults" rows both read from here.
  // ===========================================================================================

  async listEnvironmentDestinations(envId, authenticatedFetch) {
    const response = await authenticatedFetch(`${API_URL}/api/environments/${envId}/destinations`);
    return asJsonOrThrow(response, 'Failed to load environment-default destinations');
  }

  /**
   * Where every flow in the environment actually sends its answers, what the flows list labels
   * each card with. One request for the whole list rather than one per flow, and the rule that
   * decides which environment defaults a flow inherits is applied on the server, so a flow
   * missing from `flows` simply delivers to `defaults`.
   *
   * @returns {Promise<{defaults: Array<{type: string, displayName: string, inherited: boolean}>,
   *   flows: Object<string, Array<{type: string, displayName: string, inherited: boolean}>>}>}
   */
  async destinationSummary(envId, authenticatedFetch) {
    const response = await authenticatedFetch(`${API_URL}/api/environments/${envId}/destination-summary`);
    return asJsonOrThrow(response, 'Failed to load where answers are delivered');
  }

  async createEnvironmentDestination(envId, body, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/destinations`,
      { method: 'POST', body: JSON.stringify(body) }
    );
    return asJsonOrThrow(response, 'Failed to add the environment default');
  }

  async updateEnvironmentDestination(envId, destinationId, body, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/destinations/${destinationId}`,
      { method: 'PUT', body: JSON.stringify(body) }
    );
    return asJsonOrThrow(response, 'Failed to update the environment default');
  }

  async deleteEnvironmentDestination(envId, destinationId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/destinations/${destinationId}`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to delete the environment default'));
    }
  }

  async testEnvironmentDestination(envId, destinationId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/destinations/${destinationId}/test`,
      { method: 'POST' }
    );
    return asJsonOrThrow(response, 'Test send failed');
  }

  async listEnvironmentDeliveries(envId, { destinationId, limit = 20 } = {}, authenticatedFetch) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (destinationId) params.set('destinationId', destinationId);
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/deliveries?${params.toString()}`
    );
    return asJsonOrThrow(response, 'Failed to load recent deliveries');
  }

  /** Absolute URL to start the Google OAuth connect flow, open it, don't fetch it. */
  googleConnectUrl(envId) {
    return `${API_URL}/api/integrations/google/connect?envId=${encodeURIComponent(envId)}`;
  }

  async googleStatus(envId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/integrations/google/status?envId=${encodeURIComponent(envId)}`
    );
    return asJsonOrThrow(response, 'Failed to check Google connection status');
  }
}

export const deliveryService = new DeliveryService();
